"use client";

import { c as _c } from "react/compiler-runtime";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { formatMemesQuickVoteLeftThisRoundText, formatMemesQuickVoteUnratedText } from "@/hooks/memesQuickVote.helpers";
import MemesQuickVoteActionBar from "./MemesQuickVoteActionBar";
import MemesQuickVoteDescription from "./MemesQuickVoteDescription";
import MemesQuickVoteDropHeader from "./MemesQuickVoteDropHeader";
type VoteFeedbackSource = "custom-submit" | "quick-amount";
interface MemesQuickVoteControlsProps {
  readonly customValue: string;
  readonly drop: ExtendedDrop;
  readonly feedbackAmount: number | null;
  readonly feedbackSource: VoteFeedbackSource | null;
  readonly isCustomOpen: boolean;
  readonly isSubmitting: boolean;
  readonly isVoteFeedbackActive: boolean;
  readonly leftThisRoundCount: number;
  readonly latestUsedAmount: number | null;
  readonly quickAmounts: readonly number[];
  readonly uncastPower: number | null;
  readonly unratedCount: number;
  readonly votingLabel: string | null;
  readonly onCustomChange: (value: string) => void;
  readonly onCustomSubmit: () => void;
  readonly onOpenCustom: () => void;
  readonly onSkip: () => void;
  readonly onVoteAmount: (amount: number) => void;
}
function MemesQuickVoteStatPill(t0) {
  const $ = _c(3);
  const {
    children,
    tone: t1
  } = t0;
  const tone = t1 === undefined ? "secondary" : t1;
  const t2 = `tw-rounded-full tw-border tw-border-solid tw-border-white/5 tw-bg-white/[0.03] tw-px-4 tw-py-1.5 tw-shadow-sm tw-backdrop-blur-md ${tone === "primary" ? "tw-text-[13px] tw-font-bold tw-text-iron-300" : "tw-text-[13px] tw-font-medium tw-text-iron-400"}`;
  let t3;
  if ($[0] !== children || $[1] !== t2) {
    t3 = <span className={t2}>{children}</span>;
    $[0] = children;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  return t3;
}
export default function MemesQuickVoteControls(t0) {
  const $ = _c(47);
  const {
    customValue,
    drop,
    feedbackAmount,
    feedbackSource,
    isCustomOpen,
    isSubmitting,
    isVoteFeedbackActive,
    leftThisRoundCount,
    latestUsedAmount,
    quickAmounts,
    uncastPower,
    unratedCount,
    votingLabel,
    onCustomChange,
    onCustomSubmit,
    onOpenCustom,
    onSkip,
    onVoteAmount
  } = t0;
  const title = drop.metadata.find(_temp)?.data_value ?? "Untitled submission";
  const description = drop.metadata.find(_temp2)?.data_value ?? "";
  let t1;
  if ($[0] !== leftThisRoundCount) {
    t1 = formatMemesQuickVoteLeftThisRoundText(leftThisRoundCount);
    $[0] = leftThisRoundCount;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <MemesQuickVoteStatPill tone="primary">{t1}</MemesQuickVoteStatPill>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== unratedCount) {
    t3 = formatMemesQuickVoteUnratedText(unratedCount);
    $[4] = unratedCount;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t3) {
    t4 = <MemesQuickVoteStatPill>{t3}</MemesQuickVoteStatPill>;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t2 || $[9] !== t4) {
    t5 = <div className="tw-hidden tw-shrink-0 tw-flex-wrap tw-gap-2 tw-px-8 md:tw-flex md:tw-pb-6 md:tw-pt-6">{t2}{t4}</div>;
    $[8] = t2;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== drop) {
    t6 = <MemesQuickVoteDropHeader drop={drop} />;
    $[11] = drop;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== title) {
    t7 = <h2 className="tw-mb-3 tw-mt-4 tw-text-2xl tw-font-semibold tw-leading-tight tw-tracking-tight tw-text-white md:tw-text-3xl">{title}</h2>;
    $[13] = title;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== description || $[16] !== drop.id) {
    t8 = description && <MemesQuickVoteDescription key={drop.id} description={description} />;
    $[15] = description;
    $[16] = drop.id;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t7 || $[19] !== t8) {
    t9 = <div>{t7}{t8}</div>;
    $[18] = t7;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== t6 || $[22] !== t9) {
    t10 = <div className="tw-[scrollbar-gutter:stable] tw-min-h-0 tw-flex-1 tw-overflow-y-auto tw-pb-16 tw-scrollbar-thin tw-scrollbar-track-transparent tw-scrollbar-thumb-iron-700/60 desktop-hover:hover:tw-scrollbar-thumb-iron-600/80 md:tw-px-8"><div className="tw-flex tw-w-full tw-flex-col tw-gap-5">{t6}{t9}</div></div>;
    $[21] = t6;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <div className="tw-pointer-events-none tw-absolute tw-inset-x-8 tw-bottom-0 tw-h-16 tw-bg-gradient-to-t tw-from-[#0a0a0a] tw-via-[#0a0a0a]/85 tw-to-transparent" />;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== t10) {
    t12 = <div className="tw-relative tw-hidden tw-min-h-0 tw-flex-1 md:tw-flex">{t10}{t11}</div>;
    $[25] = t10;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== customValue || $[28] !== feedbackAmount || $[29] !== feedbackSource || $[30] !== isCustomOpen || $[31] !== isSubmitting || $[32] !== isVoteFeedbackActive || $[33] !== latestUsedAmount || $[34] !== onCustomChange || $[35] !== onCustomSubmit || $[36] !== onOpenCustom || $[37] !== onSkip || $[38] !== onVoteAmount || $[39] !== quickAmounts || $[40] !== uncastPower || $[41] !== votingLabel) {
    t13 = <MemesQuickVoteActionBar customValue={customValue} feedbackAmount={feedbackAmount} feedbackSource={feedbackSource} isCustomOpen={isCustomOpen} isSubmitting={isSubmitting} isVoteFeedbackActive={isVoteFeedbackActive} latestUsedAmount={latestUsedAmount} quickAmounts={quickAmounts} uncastPower={uncastPower} votingLabel={votingLabel} onCustomChange={onCustomChange} onCustomSubmit={onCustomSubmit} onOpenCustom={onOpenCustom} onSkip={onSkip} onVoteAmount={onVoteAmount} />;
    $[27] = customValue;
    $[28] = feedbackAmount;
    $[29] = feedbackSource;
    $[30] = isCustomOpen;
    $[31] = isSubmitting;
    $[32] = isVoteFeedbackActive;
    $[33] = latestUsedAmount;
    $[34] = onCustomChange;
    $[35] = onCustomSubmit;
    $[36] = onOpenCustom;
    $[37] = onSkip;
    $[38] = onVoteAmount;
    $[39] = quickAmounts;
    $[40] = uncastPower;
    $[41] = votingLabel;
    $[42] = t13;
  } else {
    t13 = $[42];
  }
  let t14;
  if ($[43] !== t12 || $[44] !== t13 || $[45] !== t5) {
    t14 = <div data-testid="quick-vote-controls-desktop-context" className="tw-flex tw-shrink-0 tw-flex-col tw-gap-0 md:tw-h-full md:tw-min-h-0 md:tw-overflow-hidden md:tw-bg-[#0a0a0a]/30">{t5}{t12}{t13}</div>;
    $[43] = t12;
    $[44] = t13;
    $[45] = t5;
    $[46] = t14;
  } else {
    t14 = $[46];
  }
  return t14;
}
function _temp2(entry_0) {
  return entry_0.data_key === "description";
}
function _temp(entry) {
  return entry.data_key === "title";
}